import React, { useState, useEffect } from 'react';
import './IPWhitelist.css';

/**
 * IP Whitelist Wrapper Component
 * 
 * This component provides client-side IP whitelisting as a backup to server-side protection.
 * Note: Server-side IP whitelisting in Caddy is the primary security layer.
 * 
 * To configure whitelisted IPs, set REACT_APP_WHITELISTED_IPS environment variable
 * as a comma-separated list: REACT_APP_WHITELISTED_IPS=127.0.0.1,192.168.1.1
 * 
 * Or set REACT_APP_IP_WHITELIST_ENABLED=false to disable client-side checking.
 */
const IPWhitelist = ({ children }) => {
  const [isAllowed, setIsAllowed] = useState(null); // null = checking, true = allowed, false = blocked
  const [clientIP, setClientIP] = useState(null);

  useEffect(() => {
    const checkIP = async () => {
      // Check if client-side IP whitelisting is enabled
      const enabled = process.env.REACT_APP_IP_WHITELIST_ENABLED !== 'false';
      
      if (!enabled) {
        setIsAllowed(true);
        return;
      }

      try {
        // Get the client's IP address
        // Using a free IP lookup service
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        const userIP = ipData.ip;
        setClientIP(userIP);

        // Get whitelisted IPs from environment variable
        const whitelistedIPs = process.env.REACT_APP_WHITELISTED_IPS
          ? process.env.REACT_APP_WHITELISTED_IPS.split(',').map(ip => ip.trim())
          : [];

        // If no whitelist is configured, allow all (server-side will handle blocking)
        if (whitelistedIPs.length === 0) {
          setIsAllowed(true);
          return;
        }

        // Check if IP is whitelisted
        const isWhitelisted = whitelistedIPs.includes(userIP) || 
                             whitelistedIPs.includes('*'); // Allow wildcard for testing

        setIsAllowed(isWhitelisted);
      } catch (error) {
        console.error('Error checking IP:', error);
        // On error, allow access (fail open) - server-side will handle security
        setIsAllowed(true);
      }
    };

    checkIP();
  }, []);

  // Show loading state while checking
  if (isAllowed === null) {
    return (
      <div className="ip-whitelist-loading">
        <div className="ip-whitelist-loading-content">
          <div className="ip-whitelist-spinner"></div>
          <p>Verifying access...</p>
        </div>
      </div>
    );
  }

  // Show blocked message if IP is not whitelisted
  if (!isAllowed) {
    return (
      <div className="ip-whitelist-blocked">
        <div className="ip-whitelist-blocked-content">
          <h1>Access Denied</h1>
          <p>Your IP address ({clientIP || 'unknown'}) is not authorized to access this website.</p>
          <p className="ip-whitelist-contact">
            If you believe this is an error, please contact the administrator.
          </p>
        </div>
      </div>
    );
  }

  // Allow access
  return <>{children}</>;
};

export default IPWhitelist;

